import { DictType } from '@pma/types'
import { toast } from '@hooks/use-toast'
import { useAuth } from '@stores/auth-store'
import { useDrawer } from '@stores/drawer-store'
import { DELETE_NOTE_ENDPOINT, SERVER_URL } from '@pma/utils/api-routes'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import { formatDate, formatDate_he, getUserFromId, parseMessage } from '@pma/utils/helpers'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { parseCookies } from 'nookies'
import { Card, CardContent, CardHeader } from '@components/common/card'
import { Separator } from '@components/common/separator'
import { PROJECTS_PAGE_ROUTE, USERS_PAGE_ROUTE } from '@pma/utils/page-routes'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@components/common/button'
import { Eye, Trash } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/common/tooltip'
import useTranslator from '@hooks/use-translator'
import ExpandTask from './expand-task'
import SimpleImage from '../image-tags/SimpleImage'

interface Props {
  note: DictType
}

export const NoteCard = ({ note }: Props) => {
  const drawer = useDrawer()
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  const navigate = useNavigate()
  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam);  

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const organizationUsers = authConfig?.organizationUsers

  const isHebrew = locale === 'he'

  const taskId = note?._task?.id
  const projectId = note?._project?.id

  const deleteNotes = async (note_id: number) => {
    const response = await axios.delete(SERVER_URL + DELETE_NOTE_ENDPOINT + `/${note_id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response?.data
  }

  const deleteNoteMutation = useMutation({
    mutationFn: ({ note_id }: DictType) => deleteNotes(note_id),
    onSuccess: (data) => {
      // Trigger Revalidate tasks
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);

      toast({
        title: translate('Success'),
        description: translate('Note deleted successfully'),
        variant: 'success',
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate('Error'),
        description: translate(errorMessage || 'Unable to delete note at this time'),
        variant: 'error',
      })
    },
  })

  const handleDelete = () => {
    deleteNoteMutation.mutate({ note_id: note?.id })
  }

  const responsibleUser = getUserFromId(note?.user_id, organizationUsers)

  const handleViewNote = () => {
    drawer.onOpen()
    drawer.setContext('notes')
    drawer.setTaskId(taskId)
    navigate(PROJECTS_PAGE_ROUTE + `/${projectId}?task=${taskId}`)
  }

  return (
    <Card className='w-full'>
      <CardHeader className='flex h-9 w-full flex-row items-center justify-between py-2'>
        <span className='text-xs text-slate-500'>{translate('Created')}</span>
        <span className='text-xs font-semibold'>
          {isHebrew ? formatDate_he(note?.created_at) : formatDate(note?.created_at)}
        </span>
      </CardHeader>
      <Separator />
      <CardContent className='flex flex-col gap-2 overflow-hidden p-2'>
        <h3 className='py-2 text-sm'>
          <p
            dangerouslySetInnerHTML={{
              __html: parseMessage(note?.note, organizationUsers),
            }}
            className='allow-list py-1 ps-3 text-sm text-slate-400'
          />
        </h3>

        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center'>
            <span className='text-xs text-slate-500'>{translate('Project')}:</span>

            <Button variant={'link'} className='text-sm' onClick={handleViewNote}>
              {note?._project?.title}
            </Button>
          </div>
          <div className='flex items-center gap-0.5'>
            <span className='pt-0.5 text-xs text-slate-500'>{translate('Task')}:</span>
            <span className='max-w-[20ch] cursor-pointer truncate text-ellipsis text-xs font-medium text-primary'>
              {note?._task?.title}
            </span>
          </div>
        </div>

        <div className='flex w-full items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <Button
              variant={'outline'}
              size={'icon'}
              className='flex h-6 w-6 flex-col items-center justify-center rounded-full bg-slate-300'
              onClick={() => {
                drawer.onOpen()
                drawer.setContext('notes')
                drawer.setTaskId(note?._task?.id)
              }}
            >
              <Eye size={14} />
            </Button>

            <Button
              variant={'outline'}
              size={'icon'}
              disabled={deleteNoteMutation.isPending}
              onClick={handleDelete}
              className='h-6 w-6 items-center justify-center rounded-full bg-slate-300 hover:border-red-500 hover:text-red-400'
            >
              <Trash size={14} />
            </Button>
            <ExpandTask task={{ ...note?._task, notes_id: [note] }} />
          </div>
          <div className='flex items-center gap-2'>
            <span className='text-xs text-slate-500'>{translate('Added by')}:</span>
            <Tooltip>
              <TooltipTrigger>
                <Link to={USERS_PAGE_ROUTE + `/${note?.user_id}`}>
                  <SimpleImage
                    title='User Profile'
                    src={responsibleUser?.user_picture?.url}
                    className='h-6 w-6 rounded-full'
                  />
                </Link>
              </TooltipTrigger>
              <TooltipContent>{responsibleUser?.name}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
